<template>
  <div class="ai-chat-sidebar" :class="{'ai-chat-sidebar-visible': visible}">
    <div class="ai-chat-header">
      <h5 class="ai-chat-title">
        <i class="material-icons text-primary">chat_bubble</i>
        {{ titleText }}
      </h5>
      <div class="ai-chat-header-buttons">
        <button @click="startNewChat" class="ai-chat-new-btn" title="Start New Chat">
          <i class="material-icons">restart_alt</i>
        </button>
        <button @click="closeSidebar" class="ai-chat-close-btn">
          <i class="material-icons">close</i>
        </button>
      </div>
    </div>

    <div class="ai-chat-content">
      <n8n-chat-widget
        ref="chatWidget"
        :webhook-url="webhookUrl"
        mode="fullscreen"
        :load-previous-session="true"
        :enable-streaming="false"
        :allow-file-uploads="false"
        :metadata="chatMetadata"
        :suggestions-enabled="suggestionsEnabled"
        :max-suggestions="maxSuggestions"
        :suggestion-buttons="suggestionButtons"
        @ready="onChatReady"
        v-if="webhookUrl"
      />
      <div v-else class="ai-chat-placeholder">
        <i class="material-icons">info</i>
        <p>Configure the webhook URL to activate AI chat</p>
      </div>
    </div>

  </div>
</template>

<script>
import N8nChatWidget from '@/components/N8nChatWidget'
import { createNewChatSession } from '../utils/aiChatSession.js'

export default {
  name: 'AiChatSidebar',
  components: {
    N8nChatWidget
  },

  data() {
    return {
      visible: true,
    }
  },

  computed: {

    webhookUrl() {
      return process.env.VUE_APP_AI_CHAT_WEBHOOK_URL || '';
    },

    chatMetadata() {
      // !! Changing any of the data-points below will refresh the chat (reactivity)
      return {
        clientId: this.$root.client?.client_hash,
        clientCode: this.$root.client?.client_code,
        bearer: localStorage.apiToken || null
      }
    },

    suggestionsEnabled() {
      // Get from page metadata if available
      return this.$root.aiChatAdditionalMetadata?.suggestionsEnabled !== false;
    },

    maxSuggestions() {
      // Get from page metadata if available
      return this.$root.aiChatAdditionalMetadata?.maxSuggestions || null;
    },

    suggestionButtons() {
      // Get suggestion buttons from page metadata if available
      return this.$root.aiChatAdditionalMetadata?.suggestionButtons || [];
    },

    titleText() {
      // Customizable title coming from page metadata; keep out of metadata payload
      // Fallback to a clearer, action-oriented label
      return this.$root.aiChatAdditionalMetadata?.title || 'Ask Eureka';
    }
  },

  mounted() {
    if (this.$root && typeof this.$root.$on === 'function') {
      this.$root.$on('ai-chat:start-new-session', this.resetChatSession);
      this.$root.$on('ai-chat:send-prompt', this.sendPromptToChat);
      this.$root.$on('ai-chat:pulse-suggestions', this.pulseSuggestionButtons);
    }
  },

  beforeDestroy() {
    if (this.$root && typeof this.$root.$off === 'function') {
      this.$root.$off('ai-chat:start-new-session', this.resetChatSession);
      this.$root.$off('ai-chat:send-prompt', this.sendPromptToChat);
      this.$root.$off('ai-chat:pulse-suggestions', this.pulseSuggestionButtons);
    }
  },

  methods: {
    closeSidebar() {
      this.$root.hideAiChatSidebar();
    },

    async startNewChat() {
      const confirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to start a new chat?', {
        title: 'Ask Eureka',
        okVariant: 'primary',
        cancelVariant: 'iso-link',
        okTitle: 'Yes, start a new chat',
        cancelTitle: 'No',
      })
      if (!confirmed) return
      this.resetChatSession();
    },

    resetChatSession() {
      createNewChatSession()

      // Remove the n8n chat sessionId from localStorage to start a new session
      localStorage.removeItem('n8n-chat/sessionId');

      // If the chat widget is available, reinitialize it
      if (this.$refs.chatWidget) {
        // Clear dynamic suggestions so we fallback to page defaults
        try { this.$refs.chatWidget.resetSuggestions(); } catch (e) {}
        // Destroy and reinitialize the chat to clear messages
        this.$refs.chatWidget.destroyChat();
        this.$nextTick(() => {
          this.$refs.chatWidget.initChat();
        });
      }
    },

    sendPromptToChat(payload = {}) {
      const prompt = typeof payload === 'string' ? payload : payload.prompt;
      const submit = typeof payload === 'object' && payload.submit === true;
      if (!prompt) return;

      const applyPrompt = (attempt = 0) => {
        const chatWidget = this.$refs.chatWidget;
        if (!chatWidget || typeof chatWidget.insertPrompt !== 'function') {
          if (attempt < 12) {
            setTimeout(() => applyPrompt(attempt + 1), 150);
          }
          return;
        }

        chatWidget.insertPrompt(prompt);
        if (submit && typeof chatWidget.submitChat === 'function') {
          setTimeout(() => chatWidget.submitChat(), 100);
        }
      };

      this.$nextTick(() => {
        setTimeout(() => applyPrompt(), 250);
      });
    },

    pulseSuggestionButtons(attempt = 0) {
      const chatWidget = this.$refs.chatWidget;
      if (!chatWidget || typeof chatWidget.pulseSuggestionButtons !== 'function') {
        if (attempt < 12) {
          setTimeout(() => this.pulseSuggestionButtons(attempt + 1), 150);
        }
        return;
      }

      const didPulse = chatWidget.pulseSuggestionButtons();
      if (!didPulse && attempt < 12) {
        setTimeout(() => this.pulseSuggestionButtons(attempt + 1), 150);
      }
    },

    onChatReady(chatInstance) {
      // Additional configurations if needed
      this.$emit('chat-ready', chatInstance);
    }
  },
  watch: {
    '$root.client': {
      handler () {
        if (this.$root.client) {
          this.resetChatSession()
        }
      }
    }
  }
}
</script>

<style lang="scss">
// Modern color palette
$primary-color: #007dfd;
$primary-hover: #007dfd;
$background: #ffffff;
$border: #e2e8f0;
$text-primary: #1e293b;
$text-secondary: #64748b;
$shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
$shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
$shadow-lg: 0px 2px 29.46px 0.54px rgba(73, 73, 73, 0.1);

// Border radius
$radius-sm: 0.375rem;
$radius-md: 0.5rem;
$radius-lg: 0.75rem;

:root {
  --chat--message--border-radius: 0.5rem !important;
}

.ai-chat-sidebar {
  position: fixed;
  top: 10px;
  right: -400px; // initially hidden
  width: 400px;
  height: calc(100vh - 20px);
  background: $background;
  box-shadow: $shadow-lg;
  z-index: 1030; // above content, below modals
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  backdrop-filter: blur(8px);
  border-radius: 20px;

  &.ai-chat-sidebar-visible {
    right: 10px;
  }

  .ai-chat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid $border;
    background-image: linear-gradient(to top left, #e4fcff, #defafd, #d9f9fc, #d3f7fa, #cef3fc, #cbeffe, #cbeaff, #cce5ff);
    min-height: 56px;
    backdrop-filter: blur(8px);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    border-bottom: 0;

    .ai-chat-title {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: $text-primary;
      font-weight: 600;
      font-size: 1rem;
      letter-spacing: -0.025em;

      .material-icons {
        padding-top: 2px;
        font-size: 1.25rem;
        transition: transform 0.2s ease;
      }
    }

    .ai-chat-header-buttons {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .ai-chat-new-btn,
    .ai-chat-close-btn {
      background: none;
      border: none;
      color: $text-secondary;
      cursor: pointer;
      padding: 0.375rem;
      border-radius: $radius-md;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      position: relative;

      .material-icons {
        font-size: 1.125rem;
        transition: transform 0.2s ease;
      }

      &:hover {
        background: rgba($primary-color, 0.1);
        color: $primary-color;
        transform: translateY(-1px);
        box-shadow: $shadow-sm;

        .material-icons {
          transform: rotate(90deg);
        }
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .chat-body, .chat-layout {
    background: #fafafa !important;

    .chat-messages-list {

      .chat-message {
        box-shadow: 0px 2px 15px 0.54px rgba(73, 73, 73, 0.1);
      }

      .chat-message-from-user {
        background-color: #e5f2fe;
        border: 1px solid #EEE;
        color: black;
      }

      .chat-message-from-bot.chat-message-typing {
        background: transparent;
        box-shadow: none;
      }
    }
  }

  .chat-layout {
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
  }

  .ai-chat-content {
    flex: 1;
    overflow: hidden;

    // Customize the N8N widget
    :deep(.n8n-chat-container) {
      height: 100%;

      // Specific adjustments for chat in sidebar
      iframe, .chat-widget {
        height: 100% !important;
        border: none !important;
        border-radius: 0 !important;
      }
    }

    .ai-chat-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: $text-secondary;
      padding: 3rem 2rem;
      text-align: center;

      .material-icons {
        font-size: 4rem;
        margin-bottom: 1.5rem;
        opacity: 0.3;
        color: $primary-color;
        animation: pulse 2s infinite;
      }

      p {
        margin: 0;
        font-size: 1rem;
        line-height: 1.5;
        font-weight: 500;
      }

      @keyframes pulse {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 0.6; }
      }
    }
  }

  .chat-action-buttons {
    background-image: linear-gradient(to top left, #e4fcff, #defafd, #d9f9fc, #d3f7fa, #cef3fc, #cbeffe, #cbeaff, #cce5ff);
    border: 0;
    padding: 3px;
    padding-bottom: 0;
    border-top-left-radius: 18px;
    border-top-right-radius: 18px;
    box-shadow: 0px -2px 29.46px 0.54px rgba(73, 73, 73, 0.25);
    margin-top: 30px;

    .action-buttons-container {
      border-top-left-radius: 16px;
      border-top-right-radius: 16px;
      background: #FFFFFF77;
      padding: 8px;
      max-height: unset;
    }
  }

  .chat-footer {
    border: 0 !important;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    background-image: linear-gradient(to bottom left, #e4fcff, #defafd, #d9f9fc, #d3f7fa, #cef3fc, #cbeffe, #cbeaff, #cce5ff) !important;

    .chat-inputs {
      margin: 3px !important;
      width: calc(100% - 6px) !important;
      background: white;
      border-bottom-left-radius: 18px !important;
      border-bottom-right-radius: 18px !important;
    }

    textarea {
      background: white;
      border-bottom-left-radius: 18px !important;
    }

    .chat-input-send-button {
      border-bottom-right-radius: 18px !important;
      color: $primary-color;
    }
  }

}

// Mobile-first responsive design
@media (max-width: 640px) {
  .ai-chat-sidebar {
    width: 100vw;
    right: -100vw;
    box-shadow: none;

    &.ai-chat-sidebar-visible {
      right: 0;
      box-shadow: 0 0 50px rgba(0, 0, 0, 0.3);
    }

    .ai-chat-header {
      padding: 1rem;
      min-height: 60px;
      border-bottom: 2px solid $border;

      .ai-chat-title {
        font-size: 1rem;
        gap: 0.5rem;

        .material-icons {
          font-size: 1.25rem;
        }
      }
    }


    // Add swipe gesture hint
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 10px;
      transform: translateY(-50%);
      width: 30px;
      height: 2px;
      background: $text-secondary;
      opacity: 0.3;
      border-radius: 1px;
    }
  }
}

// Tablet portrait
@media (min-width: 641px) and (max-width: 768px) {
  .ai-chat-sidebar {
    width: 380px;
    right: -380px;
  }
}

// Tablet landscape & small desktop
@media (min-width: 769px) and (max-width: 1024px) {
  .ai-chat-sidebar {
    width: 360px;
    right: -360px;
  }
}

// Large desktop
@media (min-width: 1025px) and (max-width: 1440px) {
  .ai-chat-sidebar {
    width: 400px;
    right: -400px;
  }
}

// Extra large screens
@media (min-width: 1441px) {
  .ai-chat-sidebar {
    width: 450px;
    right: -450px;
  }
}

// High DPI displays - increase density
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .ai-chat-sidebar {
    .ai-chat-header {
      padding: 1rem 1.25rem;
    }

    .ai-chat-toggle .material-icons {
      font-size: 1.5rem;
    }
  }
}

// Reduced motion preference
@media (prefers-reduced-motion: reduce) {
  .ai-chat-sidebar {
    transition: right 0.2s ease;

    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
}
</style>
